import type { Role } from './types';

export interface AdminUser {
  username: string;
  role: Role;
  sid_assigned: number | null;
  permissions: string[];
  created_at: string;
  created_by?: string;
  email?: string;               // remote-access email (spec 2026-09-05)
}

export interface Entitlement {
  seats_allowed: number;
  seats_used: number;
  expires_at: string;
  source: 'trial' | 'remote' | '';
  status: 'ok' | 'grace' | 'expired' | '';
}

export class SeatLimitError extends Error {
  constructor() { super('seat_limit_reached'); this.name = 'SeatLimitError'; }
}
export class LicenseExpiredError extends Error {
  constructor() { super('license_expired'); this.name = 'LicenseExpiredError'; }
}
export class UsernameTakenError extends Error {
  constructor() { super('username_exists'); this.name = 'UsernameTakenError'; }
}
export class LastOwnerError extends Error {
  constructor() { super('last_owner'); this.name = 'LastOwnerError'; }
}
export class EmailTakenError extends Error {
  constructor() { super('email_exists'); this.name = 'EmailTakenError'; }
}
export class EmailInvalidError extends Error {
  constructor() { super('email_invalid'); this.name = 'EmailInvalidError'; }
}

function readCookie(name: string): string | null {
  const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return m ? decodeURIComponent(m[1]) : null;
}

async function readErrorCode(res: Response): Promise<string> {
  // Server's JSON-error path returns {"error":"<code>"}; other errors are
  // text/plain. Try JSON first, fall back to text so the page can still
  // show a useful message.
  try {
    const body = await res.json();
    if (body && typeof body.error === 'string') return body.error;
  } catch {
    // not JSON
  }
  return '';
}

async function fetchJSON<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, { credentials: 'same-origin', ...init });
  if (res.status === 401) throw new Error('unauthenticated');
  if (res.status === 403) {
    const code = await readErrorCode(res);
    if (code === 'license_expired') throw new LicenseExpiredError();
    throw new Error('forbidden');
  }
  if (res.status === 409) {
    const code = await readErrorCode(res);
    if (code === 'seat_limit_reached') throw new SeatLimitError();
    if (code === 'username_exists')   throw new UsernameTakenError();
    if (code === 'last_owner')        throw new LastOwnerError();
    if (code === 'email_exists')      throw new EmailTakenError();
    throw new Error(code || 'conflict');
  }
  if (res.status === 400) {
    // Some 400s are JSON codes (email_invalid), most are plain text the
    // dialog shows verbatim; keep both working.
    const text = await res.text();
    let code = '';
    try {
      const j = JSON.parse(text);
      if (j && typeof j.error === 'string') code = j.error;
    } catch {
      // plain text
    }
    if (code === 'email_invalid') throw new EmailInvalidError();
    throw new Error(code || text || 'HTTP 400');
  }
  if (!res.ok) {
    const body = await res.text();
    throw new Error(body || `HTTP ${res.status}`);
  }
  // 204 has no body.
  if (res.status === 204) return undefined as unknown as T;
  return (await res.json()) as T;
}

function csrfHeaders(): Record<string, string> {
  const csrf = readCookie('pacebi_csrf');
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  if (csrf) h['X-CSRF-Token'] = csrf;
  return h;
}

export function listAdminUsers(): Promise<AdminUser[]> {
  return fetchJSON<AdminUser[]>('/api/admin/users');
}

export interface CreateUserInput {
  username: string;
  password: string;
  role: Role;
  sid_assigned?: number | null;
  permissions?: string[];
  email?: string;
}

export function createAdminUser(input: CreateUserInput): Promise<AdminUser> {
  return fetchJSON<AdminUser>('/api/admin/users', {
    method: 'POST',
    headers: csrfHeaders(),
    body: JSON.stringify(input),
  });
}

export interface UpdateUserInput {
  role?: Role;
  sid_assigned?: number | null;
  permissions?: string[];
  password?: string;
  email?: string;
}

export function updateAdminUser(username: string, input: UpdateUserInput): Promise<AdminUser> {
  return fetchJSON<AdminUser>(`/api/admin/users/${encodeURIComponent(username)}`, {
    method: 'PATCH',
    headers: csrfHeaders(),
    body: JSON.stringify(input),
  });
}

export function deleteAdminUser(username: string): Promise<void> {
  return fetchJSON<void>(`/api/admin/users/${encodeURIComponent(username)}`, {
    method: 'DELETE',
    headers: csrfHeaders(),
  });
}

export function getLicense(): Promise<Entitlement> {
  return fetchJSON<Entitlement>('/api/admin/license');
}

// Remote-access user sync (spec 2026-09-05) — shape = remotesync.Status.
export interface RemoteSyncRejection { email: string; username: string; reason: string }
export interface RemoteSyncStatus {
  enabled: boolean;
  last_attempt: string;
  last_ok: string;
  accepted: number;
  rejected: RemoteSyncRejection[];
  error_code: string;
  last_error: string;
  pending: boolean;
}

export function getRemoteSync(): Promise<RemoteSyncStatus> {
  return fetchJSON<RemoteSyncStatus>('/api/admin/remote-sync');
}

export function triggerRemoteSync(): Promise<RemoteSyncStatus> {
  return fetchJSON<RemoteSyncStatus>('/api/admin/remote-sync', { method: 'POST', headers: csrfHeaders() });
}

export interface PermissionsCatalog {
  permissions: string[];
  defaults_by_role: Record<string, string[]>;
}

export function getPermissionsCatalog(): Promise<PermissionsCatalog> {
  return fetchJSON<PermissionsCatalog>('/api/admin/permissions');
}
